import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { commandCenterApi } from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';
import { formatUploadedFull } from '../../utils/dateFormat';
import { useConfirm } from '../../utils/useConfirm';
import Icon from '../../components/Icon';
import ListRow from '../../components/ListRow';
import { toast } from '../../components/Toast';
import { AgeChip, LoadState, formatDate, openInApp, useRefetchOnFocus } from './parts';

// שליחות שנכשלו (#386): what email didn't deliver, and the way to send it again.
// Two lists over one screen: documents the bookkeeper never got, and comment
// emails. Failed invites aren't here: אנשים already has their resend.
//
// A failed document nobody will send can be marked טופל, and comes back with
// הצג גם מסמכים שסומנו טופל. One at a time, like טופל on every other console list.
// Comment rows have no טופל: they leave on their own within 30 days.
//
// A cross-tenant send is one click from a mistake, so nothing goes without a
// confirm naming the count and the customers. The server checks every item again
// right before sending it, and it decides what is ticked by default: recent, and
// not a document since marked לא תקין (server/lib/ccForwardFailures.js).

const POLL_MS = 2000;

const BLOCKED_LABEL = {
  no_inbox: 'אין כתובת דוקה',
  no_files: 'אין קבצים',
  draft: 'המסמך חזר לטיוטה',
  no_recipients: 'אין נמענים',
};

// What the counts say: a row marked טופל is on screen only because it was asked for.
const openForwards = (data) => (data?.forwards || []).filter(r => !r.handled_at);
// The server may show fewer comment emails than are open.
const commentCount = (data) => data?.comments_total ?? data?.comments?.length ?? 0;

const docLabel = (row) => `${row.doc_type || ''} ${row.doc_num || ''}`.trim();

// A comment's thread is a document or a supplier.
const threadLabel = (row) => {
  if (row.thread_type === 'document') return [docLabel(row), row.supplier_name].filter(Boolean).join(' · ') || '—';
  return row.supplier_name || '—';
};

const openRow = (kind, row) => {
  if (kind === 'forwards') return openInApp(`/document/${row.id}`, row.restaurant_id);
  return openInApp(`/${row.thread_type}/${row.reference_id}`, row.restaurant_id);
};

function RowCheck({ row, ticked, onToggle }) {
  const name = docLabel(row) || row.recipient || '';
  return (
    <label className="cc-fs-check" onClick={(e) => e.stopPropagation()}>
      <input type="checkbox" checked={ticked} disabled={!!row.blocked}
        aria-label={`בחירת ${name}`}
        onChange={(e) => onToggle(row, e.target.checked)} />
    </label>
  );
}

function BlockedPill({ row }) {
  if (!row.blocked || row.blocked === 'handled') return null;
  return <span className="cc-pill warn" style={{ marginInlineStart: 6 }}>{BLOCKED_LABEL[row.blocked] || row.blocked}</span>;
}

function InvalidPill({ row }) {
  if (row.status !== 'לא תקין') return null;
  return <span className="cc-pill warn" style={{ marginInlineStart: 6 }}>לא תקין</span>;
}

// Rows are memoised: the 2-second poll and a single tick re-render this view, and
// at 3,000 rows that was 100 ms a time on the admin's own machine.
function HandledPill({ row }) {
  if (!row.handled_at) return null;
  return (
    <span className="cc-pill ok" style={{ marginInlineStart: 6 }}
      title={[row.handled_by, row.handled_note].filter(Boolean).join(' · ') || undefined}>
      טופל {formatDate(row.handled_at)}
    </span>
  );
}

// טופל, or the way back from it. Neither is the row's click.
function HandledButton({ row, onHandle, onUndo }) {
  if (row.handled_at) {
    return (
      <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onUndo(row); }}>
        <Icon name="undo" size={13} /> החזר לרשימה
      </button>
    );
  }
  return (
    <button className="btn btn-sm btn-secondary" aria-label={`טופל ${docLabel(row)}`}
      onClick={(e) => { e.stopPropagation(); onHandle(row); }}>
      <Icon name="check" size={13} /> טופל
    </button>
  );
}

const ForwardRow = memo(function ForwardRow({ row, ticked, onToggle, onHandle, onUndo }) {
  return (
    <tr className="cc-row-link" onClick={() => openRow('forwards', row)}>
      <td><RowCheck row={row} ticked={ticked} onToggle={onToggle} /></td>
      <td><AgeChip days={row.age_days} /></td>
      <td style={{ fontWeight: 600 }}>{row.restaurant_name}</td>
      <td>{row.supplier_name || <span className="cc-pill bad">לא זוהה ספק</span>}</td>
      <td>{docLabel(row)}<InvalidPill row={row} /><BlockedPill row={row} /><HandledPill row={row} /></td>
      <td className="cc-muted"><bdi>{row.inbox || '—'}</bdi></td>
      <td className="cc-fs-error" title={row.error_message || undefined}>{row.error_message || '—'}</td>
      <td className="cc-muted cc-num">{formatUploadedFull(row.failed_at) || '—'}</td>
      <td><HandledButton row={row} onHandle={onHandle} onUndo={onUndo} /></td>
    </tr>
  );
});

function ForwardTable({ rows, isTicked, onToggle, onHandle, onUndo }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th />
            <th>גיל</th>
            <th>מסעדה</th>
            <th>ספק</th>
            <th>מסמך</th>
            <th>תישלח אל</th>
            <th>שגיאה</th>
            <th>נכשלה ב</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map(row => <ForwardRow key={row.id} row={row} ticked={isTicked(row)} onToggle={onToggle} onHandle={onHandle} onUndo={onUndo} />)}
        </tbody>
      </table>
    </div>
  );
}

const CommentRow = memo(function CommentRow({ row, ticked, onToggle }) {
  return (
    <tr className="cc-row-link" onClick={() => openRow('comments', row)}>
      <td><RowCheck row={row} ticked={ticked} onToggle={onToggle} /></td>
      <td><AgeChip days={row.age_days} /></td>
      <td style={{ fontWeight: 600 }}>{row.restaurant_name || '—'}</td>
      <td>{threadLabel(row)}<BlockedPill row={row} /></td>
      <td className="cc-muted"><bdi>{row.author || '—'}</bdi></td>
      <td className="cc-muted"><bdi>{row.recipient || '—'}</bdi></td>
      <td className="cc-fs-error" title={row.error || undefined}>{row.error || '—'}</td>
      <td className="cc-muted cc-num">{formatUploadedFull(row.failed_at) || '—'}</td>
    </tr>
  );
});

function CommentTable({ rows, isTicked, onToggle }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th />
            <th>גיל</th>
            <th>מסעדה</th>
            <th>בנוגע ל</th>
            <th>נכתבה ע״י</th>
            <th>נמענים</th>
            <th>שגיאה</th>
            <th>נכשלה ב</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => <CommentRow key={row.id} row={row} ticked={isTicked(row)} onToggle={onToggle} />)}
        </tbody>
      </table>
    </div>
  );
}

// A phone has no table: the shared two-line row, with the tick beside it. ListRow's
// root is a <button>, so the checkbox can't live inside it.
function PhoneRows({ kind, rows, isTicked, onToggle, onHandle, onUndo }) {
  return (
    <div className="m-list">
      {rows.map(row => (
        <div key={row.id} className="cc-fs-row">
          <RowCheck row={row} ticked={isTicked(row)} onToggle={onToggle} />
          <ListRow
            primary={kind === 'forwards' ? docLabel(row) : threadLabel(row)}
            meta={
              <>
                <AgeChip days={row.age_days} />
                <span>{row.restaurant_name || '—'}</span>
                <span>·</span>
                <span>{kind === 'forwards' ? (row.supplier_name || 'לא זוהה ספק') : (row.recipient || '—')}</span>
                {kind === 'forwards' && <InvalidPill row={row} />}
                <BlockedPill row={row} />
                {kind === 'forwards' && (
                  /* A span, not a button: ListRow's root is already a <button>. */
                  <span className={`cc-pill${row.handled_at ? ' ok' : ''}`} role="button" style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); if (row.handled_at) onUndo(row); else onHandle(row); }}>
                    {row.handled_at
                      ? <><Icon name="undo" size={11} /> טופל · החזר לרשימה</>
                      : <><Icon name="check" size={11} /> טופל</>}
                  </span>
                )}
              </>
            }
            onClick={() => openRow(kind, row)}
          />
        </div>
      ))}
    </div>
  );
}

// How the current run is going. A finished one shows only to whoever watched it:
// the server keeps the last run until a restart, and last week's result is noise.
function RunLine({ run, watched }) {
  if (!run || (!run.running && !watched)) return null;
  const counts = [`נשלחו ${run.sent}`, run.failed ? `נכשלו ${run.failed}` : null, run.skipped ? `דולגו ${run.skipped}` : null]
    .filter(Boolean).join(' · ');
  // One bad address doesn't stop a run (server/lib/mailResendRun.js): say which, or
  // "דולגו" would read as nothing to worry about.
  const gaveUp = run.gave_up?.length
    ? <> · דילוג על <bdi>{run.gave_up.join(', ')}</bdi> אחרי 3 כשלונות ברצף, ומה שלה נשאר ברשימה</>
    : null;
  if (run.running) {
    return <p className="cc-fs-run" role="status">שולח… {run.done} מתוך {run.total} · {counts}{gaveUp}</p>;
  }
  if (run.stopped) {
    return (
      <div className="cc-error" role="alert" style={{ marginBottom: 10 }}>
        <Icon name="alert" size={17} />
        <span>השליחה נעצרה אחרי כשלונות ברצף. {counts}{gaveUp}. {run.last_error}. מה שלא נשלח נשאר ברשימה.</span>
      </div>
    );
  }
  return <p className="cc-fs-run" role="status">הסתיים · {counts}{gaveUp}</p>;
}

export default function FailedSendsView({ kind, onKindChange, restaurantIds, docTypes, includeHandled = false, reloadKey = 0, onHandle, onCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [run, setRun] = useState(null);
  const [watched, setWatched] = useState(false);
  // Only what the admin changed; the rest follows the server's default_ticked, so
  // a refetch on focus never undoes a choice or forgets a new row.
  const [touched, setTouched] = useState({});
  const [askConfirm, confirmDialog] = useConfirm();
  const isMobile = useIsMobile();

  const loadToken = useRef(0);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setError(false);
    try {
      const r = await commandCenterApi.getMailFailures(restaurantIds, docTypes, { includeHandled });
      if (token !== loadToken.current) return;
      setData(r.data);
      setRun(r.data?.run || null);
      onCount?.(openForwards(r.data).length + commentCount(r.data));
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setData(null);
    }
  }, [restaurantIds, docTypes, includeHandled, onCount]);

  useEffect(() => { load(); }, [load, reloadKey]);
  useRefetchOnFocus(load);

  // While a run goes, ask how it is going. The answer comes from memory, so this
  // costs the single process next to nothing. Its end brings the rows back.
  const running = !!run?.running;
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      try {
        const r = await commandCenterApi.getMailResendRun();
        setRun(r.data?.run || null);
        if (!r.data?.run?.running) load();
      } catch {
        // A missed answer: the next tick asks again.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  const rows = (kind === 'comments' ? data?.comments : data?.forwards) || [];
  const keyOf = (row) => `${kind}:${row.id}`;
  const isTicked = (row) => !row.blocked && (touched[keyOf(row)] ?? row.default_ticked);
  const onUndo = useCallback(async (row) => {
    try {
      await commandCenterApi.unmarkHandled('failed-sends', row.id);
      toast('הוחזר לרשימה');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'הביטול נכשל — אפשר לנסות שוב', 'error');
    }
  }, [load]);
  const onToggle = useCallback((row, on) => setTouched(t => ({ ...t, [`${kind}:${row.id}`]: on })), [kind]);
  const sendable = rows.filter(r => !r.blocked);
  const ticked = sendable.filter(isTicked);
  const allTicked = sendable.length > 0 && ticked.length === sendable.length;
  const toggleAll = (on) => setTouched(t => {
    const next = { ...t };
    for (const row of sendable) next[keyOf(row)] = on;
    return next;
  });

  const send = async () => {
    const ids = ticked.map(r => r.id);
    try {
      const r = await commandCenterApi.resendMailFailures(
        kind === 'comments' ? { comments: ids } : { documents: ids },
      );
      setRun(r.data?.run || null);
      setWatched(true);
    } catch (err) {
      if (err.response?.data?.run) setRun(err.response.data.run);
      toast(err.response?.data?.error || 'השליחה לא התחילה — אפשר לנסות שוב', 'error');
    }
  };

  const confirmSend = () => {
    const customers = [...new Set(ticked.map(r => r.restaurant_name).filter(Boolean))];
    const what = kind === 'comments'
      ? (ticked.length === 1 ? 'מייל הערה אחד' : `${ticked.length} מיילי הערות`)
      : (ticked.length === 1 ? 'מסמך אחד להנה"ח' : `${ticked.length} מסמכים להנה"ח`);
    const minutes = Math.ceil(ticked.length / 60);
    askConfirm({
      title: 'שליחה חוזרת',
      message: `לשלוח שוב ${what}?`,
      detail: `${customers.join(', ')}. נשלח אחד בכל שנייה${ticked.length > 60 ? `, בערך ${minutes} דקות` : ''}. 3 כשלונות ברצף עוצרים את השליחה, ואם כולם לאותה כתובת מדלגים עליה וממשיכים.`,
      confirmLabel: 'שלח שוב',
      tone: 'primary',
      onConfirm: send,
    });
  };

  return (
    <>
      <div className="view-tabs" style={{ margin: '0 0 12px' }}>
        <button className={`view-tab${kind !== 'comments' ? ' is-active' : ''}`} onClick={() => onKindChange('forwards')}>
          הנה"ח{data ? ` (${openForwards(data).length})` : ''}
        </button>
        <button className={`view-tab${kind === 'comments' ? ' is-active' : ''}`} onClick={() => onKindChange('comments')}>
          הערות{data ? ` (${commentCount(data)})` : ''}
        </button>
      </div>

      <RunLine run={run} watched={watched} />

      <LoadState
        loading={data === null && !error}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyMessage={kind === 'comments' ? 'אין מיילי הערות שנכשלו' : 'אין מסמכים שנכשלו בשליחה להנה"ח'}
      >
        <div className="cc-fs-actions">
          <label className="cc-fs-all">
            <input type="checkbox" checked={allTicked} disabled={!sendable.length}
              onChange={(e) => toggleAll(e.target.checked)} />
            בחר הכל
          </label>
          <span className="cc-muted">נבחרו {ticked.length} מתוך {sendable.length}</span>
          <button className="btn btn-sm btn-primary" disabled={!ticked.length || running} onClick={confirmSend}>
            <Icon name="send" size={14} /> שלח שוב
          </button>
        </div>
        {kind === 'comments' && data && rows.length < commentCount(data) && (
          <p className="cc-muted" style={{ margin: '0 0 10px' }}>
            מוצגים {rows.length.toLocaleString('he-IL')} מתוך {commentCount(data).toLocaleString('he-IL')} מיילים, קודם אלה שאפשר לשלוח. אפשר לצמצם לפי מסעדה.
          </p>
        )}
        {kind !== 'comments' && data && rows.length >= data.row_limit && (
          <p className="cc-muted" style={{ margin: '0 0 10px' }}>
            מוצגות {data.row_limit.toLocaleString('he-IL')} השורות הישנות ביותר. אפשר לצמצם לפי מסעדה או סוג מסמך.
          </p>
        )}
        {isMobile
          ? <PhoneRows kind={kind} rows={rows} isTicked={isTicked} onToggle={onToggle} onHandle={onHandle} onUndo={onUndo} />
          : kind === 'comments'
            ? <CommentTable rows={rows} isTicked={isTicked} onToggle={onToggle} />
            : <ForwardTable rows={rows} isTicked={isTicked} onToggle={onToggle} onHandle={onHandle} onUndo={onUndo} />}
      </LoadState>
      {confirmDialog}
    </>
  );
}
